import { logGreyError } from "owa-analytics";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";

/**
 * Detached React roots (DOM memory leak) tracker.
 *
 * Why we need this:
 * When we mount a React tree into a container element (e.g. via ReactDOM.render /
 * createRoot), we expect the owning code to later unmount it before throwing away the
 * container.  If a container is removed from the document but its React tree is never
 * unmounted, React keeps the detached DOM subtree (and everything its component closures
 * reference) alive forever.  These "detached React components" are a common, hard-to-spot
 * source of memory leaks in the app.  This tracker detects those leaks in real user
 * sessions and reports them via telemetry so we can find and fix the leaking mount sites.
 *
 * How it works:
 * - trackMountedComponent() records each mounted container together with the callstack at
 *   mount time (so the telemetry tells us exactly which code path created the leak).
 * - untrackMountedComponent() removes a container when it is properly unmounted, so clean
 *   mount/unmount pairs are never reported.
 * - A timer (started lazily on the first mount) calls checkDetachedComponents() every
 *   5 minutes.  A container is only flagged after it has been observed disconnected from
 *   the document across two consecutive checks.  This two-pass debounce avoids false
 *   positives from containers that are temporarily detached and re-attached (e.g. virtualized
 *   lists or transient re-parenting) within a single interval.
 * - Before logging, isConnectedToReactComponent() confirms React still considers the node
 *   mounted (via its internal __react* expando properties).  A node that is disconnected
 *   AND still owned by React is a genuine leak; one React has already unmounted is not.
 *
 * The reported telemetry payload is the set of mount-time callstacks for the leaked
 * components, which lets us attribute each leak back to its originating code.
 */

const TIMER = 5 * 60 * 1000; // 5 minutes

/** One tracked React mount: a container element and the bookkeeping we need to decide if its tree leaked. */
interface MountedComponent {
    /** The DOM element React was mounted into. Used as the identity key for track/untrack and the leak check. */
    container: Element;
    /** Callstack captured at mount time, reported via telemetry so a detected leak can be traced to its mount site. */
    mountCallstack: string;
    /**
     * Whether the container was observed disconnected from the document on the previous check.
     * Drives the two-pass debounce: a container must be detached across two consecutive checks
     * before it is treated as a genuine leak, avoiding false positives from transient detachment.
     */
    isDetached: boolean;
}
let isTrackerStarted = false;

export function trackMountedComponent(container: Element): void {
    if (!isTrackerStarted) {
        startMountedComponentTracker();
        isTrackerStarted = true;
    }
    
    const mountCallstack = new Error("GetCallstack").stack ?? "";
    const mountedComponent: MountedComponent = {
        container,
        mountCallstack,
        isDetached: false,
    };
    
    mountedComponents.push(mountedComponent);
}

export function untrackMountedComponent(container: Element): void {
    const index = mountedComponents.findIndex(mc => mc.container === container);
    
    if (index !== -1) {
        mountedComponents.splice(index, 1);
    }
}

function checkDetachedComponents(): void {
    // Use a Set so duplicate callstacks within this batch (e.g. many leaked instances mounted
    // from the same code path) collapse to a single entry.
    const logs = new Set<string>();
    
    for (let i = mountedComponents.length - 1; i >= 0; i--) {
        const mc = mountedComponents[i];
        
        if (!mc.container.isConnected) {
            if (!mc.isDetached) {
                // Just in case the component gets re-attached, we won't log it as detached again until it gets detached again.
                mc.isDetached = true;
            }
            else {
                // This component was detached the last time we checked, and it's still detached.
                // Skip callstacks we've already reported in a previous batch so the same leak site
                // isn't logged repeatedly.
                if (isConnectedToReactComponent(mc.container) &&
                    !loggedCallstacks.has(mc.mountCallstack)) {
                    // React still considers this node mounted, so it's a genuine detached leak.  Log it.
                    logs.add(mc.mountCallstack);
                }
                // Whether or not React still owns it, stop tracking it.
                mountedComponents.splice(i, 1);
            }
        }
        else {
            // The component was detached the last time we checked, but it's now re-attached.  Clear the detached flag.
            mc.isDetached = false;
        }
    }
    
    if (logs.size > 0) {
        logGreyError("DetachedReactComponent", getGlobalSettingsAccountMailboxInfo(), undefined, {
            mountCallstacks: Array.from(logs).join("\n\n"),
            count: logs.size,
        });
        
        // Remember what we've logged so an identical callstack isn't reported again in a later check.
        for (const callstack of logs) {
            loggedCallstacks.add(callstack);
        }
    }
}

function isConnectedToReactComponent(node: Element): boolean {
    // React attaches internal expando properties (e.g. __reactContainer$..., __reactFiber$...,
    // __reactProps$...) to the DOM nodes it manages, and deletes them when the corresponding
    // component unmounts.  If none of those properties remain with a truthy value, React no
    // longer considers this node mounted, so we treat it as unmounted rather than a leak.
    return Object.keys(node).some(key => key.startsWith("__react") && !!(node as unknown as Record<string, unknown>)[key]);
}

function startMountedComponentTracker(): void {
    setInterval(checkDetachedComponents, TIMER); // Check every 5 minutes
}

const mountedComponents: MountedComponent[] = [];

// Mount-time callstacks we've already reported, so the same leak site isn't logged repeatedly.
const loggedCallstacks = new Set<string>();
